import axios, { type AxiosInstance } from 'axios';
import { action, computed, flow, makeObservable, observable } from 'mobx';

import { EventEmitter } from '../../shared/EventEmitter';
import type { IDirectoriesService, TDictObject } from '../../shared/IDirectoriesService';
import { SockClientService } from '../../shared/SockClientService';
import type { Wellbores } from '../../shared/stores/WellboresStore';
import type { SettingsStore } from '../stores/SettingsStore';

import { AutoAnomaliesApi } from './AutoAnomaliesApi';
import { AutoAnomaliesUiState } from './ManualAnomaliesUiState';
import type { TAutoAnomalyRowData, TAutoAnomalyRowDto } from './types';

type TAutoAnomaliesMessage = {
  type: 'CREATED' | 'DELETED';
  id: string; // uuid
  data: TAutoAnomalyRowDto;
};

const mapAutoAnomalyRowDtoToData = (dto: TAutoAnomalyRowDto): TAutoAnomalyRowData => {
  return {
    id: dto.id,
    wellId: dto.wellId,
    wellboreId: dto.wellboreId,
    indexType: dto.indexType,
    riskLevel: dto.riskLevel,
    anomalyTypeId: dto.anomalyType?.typeId,
    anomalySubtypeId: dto.anomalyType?.subtypeId,
    sourceType: dto.sourceData.type,
    sourceOption: dto.sourceData.type === 'GENERAL_PARAMETERS' ? dto.sourceData.param : dto.sourceData.code,
    bitDepth: dto.bitDepth,
    holeDepth: dto.holeDepth,
    unitId: dto.unitId,
    createdAt: dto.createdAt,
    createdBy: dto.createdBy,
    anomalyAt: dto.anomalyAt,
    notification: dto.notification,
    recommendation: dto.recommendation,
    minimumReferenceCurve: dto.minimumReferenceCurve,
    maximumReferenceCurve: dto.maximumReferenceCurve,
    stageId: dto.stageId,
    wellboreTypeId: dto.wellboreTypeId,
    trajectoryTypeId: dto.trajectoryTypeId,
    sectionTypeId: dto.sectionTypeId,
    stratUnitTypeId: dto.stratUnitTypeId,
    reservoirId: dto.reservoirId,
    runTypeId: dto.runTypeId,
    operationId: dto.operationId,
    limitType: dto.limitType,
    valueLimit: dto.values?.limit,
    valueFact: dto.values?.fact,
    valueAbsoluteDiff: dto.values?.absoluteDiff,
    valueRelativeDiff: dto.values?.relativeDiff,
  };
};

type TEventMap = {
  newAutoAnomalies: TAutoAnomalyRowData[];
  deleteAutoAnomalies: string[];
  updateAutoAnomalies: TAutoAnomalyRowData[];
};

export class AutoAnomaliesService {
  private static readonly TABLE_UPDATE_THROTTLE_MS = 600;

  private readonly api: AutoAnomaliesApi;
  private readonly abortControllers = new Map<string, AbortController>();
  private readonly socket: SockClientService<TAutoAnomaliesMessage>;
  private readonly pendingSocketMessages: TAutoAnomaliesMessage[] = [];
  private socketMessageFlushTimeout: ReturnType<typeof setTimeout> | null = null;
  readonly uiState = new AutoAnomaliesUiState();
  readonly eventEmitter = new EventEmitter<TEventMap>();

  topicAutoAnomalies;

  @observable loading = false;
  rowsInternal: TAutoAnomalyRowData[] = [];

  deletedAutoAnomaliesIds: string[] = [];

  @observable anomalyTypes: TDictObject[] = [];
  @observable anomalySubtypes: TDictObject[] = [];
  @observable workClassifierStageTypes: TDictObject[] = [];
  @observable wellboreTypes: TDictObject[] = [];
  @observable trajectoryTypes: TDictObject[] = [];
  @observable sectionTypes: TDictObject[] = [];
  @observable stratUnitTypes: TDictObject[] = [];
  @observable reservoirTypes: TDictObject[] = [];
  @observable runTypes: TDictObject[] = [];
  @observable unitsList: TDictObject[] = [];
  @observable autosupervisorOperations: TDictObject[] = [];
  @observable autosupervisorKPIstates: TDictObject[] = [];

  @observable areDirectoriesLoaded = false;

  constructor(
    private readonly wellId: number,
    agent: AxiosInstance,
    public readonly settingsStore: SettingsStore,
    public readonly wellbores: Wellbores,
    private readonly directoryService: IDirectoriesService,
    getAccessToken: () => string | null,
  ) {
    this.api = new AutoAnomaliesApi(agent);
    this.topicAutoAnomalies = `/topic/anomalies/well/${wellId}/auto`;
    this.socket = new SockClientService<TAutoAnomaliesMessage>('/anomaly-registry/api/ws', getAccessToken);
    makeObservable(this);
  }

  @computed
  get rows(): TAutoAnomalyRowData[] {
    return this.rowsInternal;
  }

  get hasUnsavedChanges(): boolean {
    return !!this.deletedAutoAnomaliesIds.length;
  }

  setEditing(value: boolean) {
    this.uiState.setEditing(value);
  }

  @flow.bound
  private async *fetchDirectories() {
    try {
      const [
        anomalyTypes,
        anomalySubtypes,
        workClassifierStageTypes,
        wellboreTypes,
        trajectoryTypes,
        sectionTypes,
        stratUnitTypes,
        reservoirTypes,
        runTypes,
        unitsList,
        autosupervisorOperations,
        autosupervisorKPIstates,
      ] = await this.directoryService.loadObjects([
        'Common_AnomalyTypes',
        'Common_AnomalySubTypes',
        'GOreport_WorkClassifier_Stage',
        'Common_WellboreType',
        'Common_TrajectoryType',
        'Common_SectionType',
        'Common_StratUnitType',
        'Common_Reservoir',
        'Common_RunType',
        'Common_Uom',
        'Common_AutosupervisorOperations',
        'Common_AutosupervisorKPIstates',
      ]);
      yield;

      this.anomalyTypes = anomalyTypes ?? [];
      this.anomalySubtypes = anomalySubtypes ?? [];
      this.workClassifierStageTypes = workClassifierStageTypes ?? [];
      this.wellboreTypes = wellboreTypes ?? [];
      this.trajectoryTypes = trajectoryTypes ?? [];
      this.sectionTypes = sectionTypes ?? [];
      this.stratUnitTypes = stratUnitTypes ?? [];
      this.reservoirTypes = reservoirTypes ?? [];
      this.runTypes = runTypes ?? [];
      this.unitsList = unitsList ?? [];
      this.autosupervisorOperations = autosupervisorOperations ?? [];
      this.autosupervisorKPIstates = autosupervisorKPIstates ?? [];

      this.areDirectoriesLoaded = true;
    } catch (e) {
      yield;
      console.error(e);
    }
  }

  @flow.bound
  private *fetchAnomalies() {
    this.loading = true;
    this.abortControllers.get('fetch')?.abort();
    const ac = new AbortController();
    this.abortControllers.set('fetch', ac);

    try {
      const list: TAutoAnomalyRowDto[] = yield this.api.fetchList(this.wellId, ac.signal);
      this.addAnomalies(list);
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
      console.error(e);
    } finally {
      this.loading = false;
      this.abortControllers.delete('fetch');
    }
  }

  @flow.bound
  *save() {
    this.loading = true;
    try {
      yield this.api.deleteRows(this.deletedAutoAnomaliesIds);
      this.deletedAutoAnomaliesIds = [];
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error(e);
      }
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  deleteRows(entityIds: string[]) {
    if (!entityIds.length) {
      return;
    }

    const idSet = new Set(entityIds);
    const nextRows: TAutoAnomalyRowData[] = [];
    const deletedIds: string[] = [];

    for (const anomaly of this.rowsInternal) {
      if (idSet.has(anomaly.id)) {
        deletedIds.push(anomaly.id);
      } else {
        nextRows.push(anomaly);
      }
    }

    if (!deletedIds.length) {
      return;
    }

    this.rowsInternal = nextRows;
    this.deletedAutoAnomaliesIds.push(...deletedIds);

    this.eventEmitter.emit('deleteAutoAnomalies', deletedIds);
  }

  addAnomalies(list: TAutoAnomalyRowDto[]) {
    const mappedList = list.map(mapAutoAnomalyRowDtoToData);
    this.rowsInternal = [...this.rowsInternal, ...mappedList];
    this.eventEmitter.emit('newAutoAnomalies', mappedList);
  }

  private flushSocketMessages = () => {
    this.socketMessageFlushTimeout = null;

    if (!this.pendingSocketMessages.length) {
      return;
    }

    const createdById = new Map<string, TAutoAnomalyRowDto>();
    const deletedIds = new Set<string>();

    for (const message of this.pendingSocketMessages.splice(0)) {
      if (message.type === 'CREATED') {
        deletedIds.delete(message.id);
        createdById.set(message.id, message.data);
      } else if (message.type === 'DELETED') {
        createdById.delete(message.id);
        deletedIds.add(message.id);
      }
    }

    if (createdById.size) {
      this.addAnomalies([...createdById.values()]);
    }

    if (deletedIds.size) {
      this.deleteRows([...deletedIds]);
    }
  };

  autoAnomaliesMassageCallback = (data: TAutoAnomaliesMessage) => {
    this.pendingSocketMessages.push(data);

    if (!this.socketMessageFlushTimeout) {
      this.socketMessageFlushTimeout = setTimeout(
        this.flushSocketMessages,
        AutoAnomaliesService.TABLE_UPDATE_THROTTLE_MS,
      );
    }
  };

  async loadInitialData() {
    await Promise.all([this.fetchAnomalies(), this.fetchDirectories(), this.wellbores.fetchWellbores(this.wellId)]);
  }

  @flow.bound
  *discardChanges() {
    this.deletedAutoAnomaliesIds = [];

    yield this.fetchAnomalies();
  }

  init = (): VoidFunction => {
    void this.loadInitialData();

    this.socket.init(true);
    this.socket.subscribe(this.topicAutoAnomalies, this.autoAnomaliesMassageCallback);

    return () => {
      this.abortControllers.forEach((c) => c.abort());
      if (this.socketMessageFlushTimeout) {
        clearTimeout(this.socketMessageFlushTimeout);
      }
      this.flushSocketMessages();
      this.socket.unsubscribe(this.topicAutoAnomalies, this.autoAnomaliesMassageCallback);
      this.socket.destroy();
    };
  };
}
